import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ContactPayload,
    CopyPayload,
    PublicProfileSettingsAPI,
    VisibilityPayload,
} from "../infrastructure/api/PublicProfileSettings.api";
import { PublicProfileSettings } from "../domain/types";

const KEY = ["public-profile-settings"];

export const usePublicProfileSettings = () =>
    useQuery({
        queryKey: KEY,
        queryFn: async () => {
            const { data } = await PublicProfileSettingsAPI.get();
            return data;
        },
    });

/** Every mutation returns the whole profile, so the cache is replaced rather
 *  than patched — the server is the only thing that knows what is still
 *  missing for publication. */
function useProfileMutation<TInput>(
    call: (input: TInput) => Promise<{ data: PublicProfileSettings }>,
) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: TInput) => (await call(input)).data,
        onSuccess: (profile) => queryClient.setQueryData(KEY, profile),
    });
}

export const useUpdateCopy = () =>
    useProfileMutation<CopyPayload>(PublicProfileSettingsAPI.updateCopy);

export const useUpdateContact = () =>
    useProfileMutation<ContactPayload>(PublicProfileSettingsAPI.updateContact);

export const useUpdateVisibility = () =>
    useProfileMutation<VisibilityPayload>(PublicProfileSettingsAPI.updateVisibility);

export const usePublish = () =>
    useProfileMutation<void>(() => PublicProfileSettingsAPI.publish());

export const useUnpublish = () =>
    useProfileMutation<void>(() => PublicProfileSettingsAPI.unpublish());

export const useRemovePhoto = () =>
    useProfileMutation<void>(() => PublicProfileSettingsAPI.removePhoto());

/**
 * Upload straight to storage with the signed URL, then tell the API. The image
 * bytes never pass through our own server.
 */
export const useUploadPhoto = () =>
    useProfileMutation<File>(async (file: File) => {
        const { data: issued } = await PublicProfileSettingsAPI.photoUploadUrl(
            file.type,
            file.size,
        );
        const upload = await fetch(issued.upload_url, {
            method: "PUT",
            body: file,
            headers: { "Content-Type": file.type },
        });
        // fetch only rejects on network/CORS failure; a 403 from an expired or
        // mis-signed URL resolves normally. Without this check we would confirm
        // an object that storage never accepted.
        if (!upload.ok) {
            throw new Error(
                `Photo upload rejected by storage (${upload.status} ${upload.statusText})`,
            );
        }
        return PublicProfileSettingsAPI.confirmPhoto(issued.object_name);
    });
